import { useQuery } from '@tanstack/react-query';
import { createFileRoute, getRouteApi } from '@tanstack/react-router';
import { useState } from 'react';

import { LoadingIndicator } from '@/components/base/loading-indicator/loading-indicator';
import { participantBookingQueryOptions } from '@/hooks/api/use-participant-booking';
import { profileQueryOptions, useProfile } from '@/hooks/api/use-profile';
import { getClient } from '@/utils/get-api-client';

import {
  BookingBlocked,
  BookingPageShell,
} from './-components/booking/booking-page-shell';
import {
  BookingScheduler,
  type SchedulableBooking,
} from './-components/booking/booking-scheduler';

/**
 * Move a booked session: the legacy
 * `/respondents/schedule/:bookingUid/reschedule/:id` page. Refused inside
 * the lead time unless the researcher enabled rescheduling for this
 * participant; the server enforces the same rule on submit.
 */
export const Route = createFileRoute(
  '/participants/_authenticated/projects/$projectId/reschedule',
)({
  loader: async ({ context, params }) => {
    // The authenticated layout has already turned away anyone without a
    // user; the root context type still admits null.
    const userId = context.user?.id;
    if (userId) {
      await Promise.all([
        context.queryClient.ensureQueryData(
          participantBookingQueryOptions(params.projectId, userId),
        ),
        // Pre-fills the booking phone; loaded up front because the form
        // takes its initial value on mount.
        context.queryClient.ensureQueryData(
          profileQueryOptions(userId, getClient(userId)),
        ),
      ]);
    }
  },
  component: ReschedulePage,
});

const authenticatedRoute = getRouteApi('/participants/_authenticated');

function ReschedulePage() {
  const { projectId } = Route.useParams();
  const { user } = authenticatedRoute.useRouteContext();
  const booking = useQuery(participantBookingQueryOptions(projectId, user.id));
  const profile = useProfile(user.id).data?.profile;
  // After the move, the refetched booking can sit inside the lead time and
  // the gate below would refuse. The scheduler stays on screen with the
  // state it moved from, so it can show its confirmation.
  const [moved, setMoved] = useState<SchedulableBooking | null>(null);

  const data = booking.data;
  const reschedulable =
    data &&
    !data.blockedReason &&
    data.event &&
    data.booking &&
    data.rescheduleAllowed
      ? { ...data, event: data.event }
      : null;
  const shown = moved ?? reschedulable;

  return (
    <BookingPageShell projectId={projectId} wide>
      {shown?.booking ? (
        <BookingScheduler
          projectId={projectId}
          userId={user.id}
          booking={shown}
          participantName={{
            firstName: user.firstName,
            lastName: user.lastName,
          }}
          defaultPhone={profile?.phone ?? undefined}
          defaultCountry={profile?.location?.city?.country}
          original={shown.booking}
          onBooked={() => setMoved(shown)}
        />
      ) : !data ? (
        <LoadingIndicator />
      ) : data.blockedReason || !data.event ? (
        <BookingBlocked reason={data.blockedReason ?? 'NO_CALENDAR'} />
      ) : !data.booking ? (
        <BookingBlocked reason="NO_BOOKING" />
      ) : (
        <BookingBlocked reason="RESCHEDULE_TOO_LATE" />
      )}
    </BookingPageShell>
  );
}
